import React, { useState } from 'react';
import { Colors } from 'library';
import { RModal, RModalFooter } from './RModal';
import { RPillButton } from './RPillButton';
import { RStack } from './RStack';
import { RText } from './RText';
import { RToast } from './RToast';

interface RDeleteWithConfirmationProps {
    button: React.ReactElement;
    message: string;
    onSubmit: () => Promise<void>;
    title?: string;
    confirmLabel?: string;
    successMessage?: string;
    errorMessage?: string;
}

export function RDeleteWithConfirmation({
    button,
    message,
    onSubmit,
    title = 'Confirm Delete',
    confirmLabel = 'Delete',
    successMessage = 'Deleted',
    errorMessage = 'Could not delete',
}: RDeleteWithConfirmationProps) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<string | null>(null);

    function showToast(msg: string) {
        setToast(msg);
        setTimeout(() => setToast(null), 3000);
    }

    async function handleConfirm() {
        setSubmitting(true);
        try {
            await onSubmit();
            setOpen(false);
            showToast(successMessage);
        } catch {
            showToast(errorMessage);
        } finally {
            setSubmitting(false);
        }
    }

    const trigger = React.cloneElement(button, {
        onClick: (e: React.MouseEvent) => {
            button.props.onClick?.(e);
            setOpen(true);
        },
    });

    return (
        <>
            {trigger}
            {open && (
                <RModal title={title} onClose={() => !submitting && setOpen(false)} width={440}>
                    <RStack gap="md">
                        <RText>{message}</RText>
                        <RModalFooter>
                            <RPillButton variant="secondary" onClick={() => setOpen(false)} disabled={submitting}>
                                Cancel
                            </RPillButton>
                            <RPillButton onClick={handleConfirm} disabled={submitting} color={Colors.mediumRed}>
                                {submitting ? 'Deleting...' : confirmLabel}
                            </RPillButton>
                        </RModalFooter>
                    </RStack>
                </RModal>
            )}
            <RToast message={toast} />
        </>
    );
}
